import type { CancelToken } from 'axios';
import type { Clock, TimeoutCookie } from '../types/clock';

/**
 * An asynchronous sleep operation that can be cancelled.
 *
 * @deprecated This leaks memory by accumulating new promises when invoking ".then"
 *             Use the abortableSleep function instead. You can use cancelTokenAsAbortSignal()
 *             to get an AbortSignal from a CancelToken without the memory leak problem.
 */
export function sleep(durationMs: number, ct: CancelToken): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      resolve();
    }, durationMs);
    ct.promise.then(reason => {
      reject(reason);
      clearTimeout(id);
    }).catch(() => {});
  });
}

/**
 * TODO: Remove the performanceNow parameter when we've ported everything to
 * Node 14, including the version of Kelda that Nan is using for testing.
 */
export function monotonicClock(monotonicNowFunc: () => number): Clock {
  return {
    absoluteNow() {
      return Date.now();
    },
    monotonicNow() {
      return monotonicNowFunc();
    },
    setTimeout: (callback: () => void, ms: number) => {
      return setTimeout(callback, ms);
    },
    clearTimeout: (cookie: TimeoutCookie) => {
      clearTimeout(cookie);
    }
  };
}

/**
 * Return performance.now(), or as a fallback Date.now().
 */
export function portableMonotonicNowFunction(): () => number {
  if (typeof performance === 'object') {
    return () => performance.now();
  }

  // Note: The portable clock is not really monotonic on Node 14 and older.
  return () => Date.now();
}

export function portableClock() {
  return monotonicClock(portableMonotonicNowFunction());
}
